import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { 
  Bot, Send, Users, Truck, Building2, Play, Pause, 
  RefreshCw, AlertTriangle, CheckCircle, Clock, Zap,
  Mail, BarChart3, Target
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AIDispatcherAdmin = () => {
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  
  // New campaign form
  const [newCampaign, setNewCampaign] = useState({
    campaign_name: '',
    target_audience: 'both',
    subject: '',
    email_body: '',
    use_template: 'broker_introduction'
  });

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, campaignsRes] = await Promise.all([
        fetch(`${API}/api/ai-dispatcher-admin/execute-task`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ task: 'get_stats' })
        }),
        fetch(`${API}/api/ai-dispatcher-admin/campaigns`)
      ]);
      
      const statsData = await statsRes.json();
      const campaignsData = await campaignsRes.json();
      
      setStats(statsData.stats);
      setCampaigns(campaignsData.campaigns || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const startQuickCampaign = async (task) => {
    if (!confirm(`Start ${task} campaign? This will send emails to all matching recipients.`)) return;
    
    setCreating(true);
    try {
      const response = await fetch(`${API}/api/ai-dispatcher-admin/execute-task`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task })
      });
      
      const data = await response.json();
      alert(`Campaign started! ID: ${data.campaign?.campaign_id}`);
      fetchData();
    } catch (error) {
      alert('Error starting campaign: ' + error.message);
    } finally {
      setCreating(false);
    }
  };

  const pauseResumeCampaign = async (campaignId, action) => {
    try {
      await fetch(`${API}/api/ai-dispatcher-admin/campaign/${campaignId}/${action}`, {
        method: 'POST'
      });
      fetchData();
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'running': { color: 'bg-green-500', icon: <Play className="w-3 h-3" /> },
      'completed': { color: 'bg-blue-500', icon: <CheckCircle className="w-3 h-3" /> },
      'paused_quota': { color: 'bg-yellow-500', icon: <Pause className="w-3 h-3" /> },
      'paused_manual': { color: 'bg-yellow-500', icon: <Pause className="w-3 h-3" /> },
      'queued': { color: 'bg-zinc-500', icon: <Clock className="w-3 h-3" /> },
      'cancelled': { color: 'bg-red-500', icon: <AlertTriangle className="w-3 h-3" /> }
    };
    
    const config = statusConfig[status] || { color: 'bg-zinc-500', icon: null };
    
    return (
      <Badge className={`${config.color} text-white flex items-center gap-1`}>
        {config.icon} {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
              <Bot className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">AI Dispatcher Admin</h1>
              <p className="text-zinc-400">Autonomous bulk email campaigns</p>
            </div>
          </div>
          <Button onClick={fetchData} variant="outline" className="border-zinc-700">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Brokers</p>
                  <p className="text-2xl font-bold">{stats?.brokers_with_email?.toLocaleString()}</p>
                </div>
                <Building2 className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Drivers</p>
                  <p className="text-2xl font-bold">{stats?.drivers_with_email?.toLocaleString()}</p>
                </div>
                <Truck className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Daily Quota</p>
                  <p className="text-2xl font-bold">{stats?.ses_quota?.remaining_today}</p>
                  <p className="text-xs text-zinc-500">of {stats?.ses_quota?.max_24_hour_send}/day</p>
                </div>
                <Mail className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Send Rate</p>
                  <p className="text-2xl font-bold">{stats?.ses_quota?.max_send_rate}/sec</p>
                </div>
                <Zap className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-500" />
              Quick Campaigns (One-Click)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <Button
                onClick={() => startQuickCampaign('send_broker_outreach')}
                disabled={creating || stats?.ses_quota?.remaining_today <= 0}
                className="h-20 bg-blue-600 hover:bg-blue-700 flex flex-col items-center justify-center"
              >
                <Building2 className="w-6 h-6 mb-1" />
                <span>Email ALL Brokers</span>
                <span className="text-xs opacity-75">{stats?.brokers_with_email?.toLocaleString()} recipients</span>
              </Button>
              
              <Button
                onClick={() => startQuickCampaign('send_driver_outreach')}
                disabled={creating || stats?.ses_quota?.remaining_today <= 0}
                className="h-20 bg-green-600 hover:bg-green-700 flex flex-col items-center justify-center"
              >
                <Truck className="w-6 h-6 mb-1" />
                <span>Email ALL Drivers</span>
                <span className="text-xs opacity-75">{stats?.drivers_with_email?.toLocaleString()} recipients</span>
              </Button>
              
              <Button
                onClick={() => startQuickCampaign('send_all_outreach')}
                disabled={creating || stats?.ses_quota?.remaining_today <= 0}
                className="h-20 bg-purple-600 hover:bg-purple-700 flex flex-col items-center justify-center"
              >
                <Users className="w-6 h-6 mb-1" />
                <span>Email EVERYONE</span>
                <span className="text-xs opacity-75">{((stats?.brokers_with_email || 0) + (stats?.drivers_with_email || 0)).toLocaleString()} recipients</span>
              </Button>
            </div>
            
            {stats?.ses_quota?.remaining_today <= 0 && (
              <div className="bg-red-900/30 border border-red-800 rounded-lg p-4 flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <div>
                  <p className="font-medium text-red-400">Daily quota exhausted</p>
                  <p className="text-sm text-red-300">AWS SES production access pending. Current limit: {stats?.ses_quota?.max_24_hour_send}/day</p>
                </div>
              </div>
            )}
            
            {stats?.ses_quota?.max_24_hour_send === 200 && (
              <div className="bg-yellow-900/30 border border-yellow-800 rounded-lg p-4 flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                <div>
                  <p className="font-medium text-yellow-400">Sandbox Mode Active</p>
                  <p className="text-sm text-yellow-300">Limited to 200 emails/day. Once AWS approves production access, limit increases to 50,000+/day.</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Active Campaigns */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-green-500" />
              Campaigns
            </CardTitle>
          </CardHeader>
          <CardContent>
            {campaigns.length === 0 ? (
              <p className="text-zinc-500 text-center py-8">No campaigns yet. Start one above!</p>
            ) : (
              <div className="space-y-4">
                {campaigns.map((campaign) => (
                  <div key={campaign.id} className="bg-zinc-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-medium">{campaign.name}</p>
                        <p className="text-sm text-zinc-400">{campaign.target_audience} • {new Date(campaign.created_at).toLocaleDateString()}</p>
                      </div>
                      {getStatusBadge(campaign.status)}
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4 text-sm mb-3">
                      <div>
                        <p className="text-zinc-500">Total</p>
                        <p className="font-medium">{campaign.total_recipients?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500">Sent</p>
                        <p className="font-medium text-green-500">{campaign.sent?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500">Failed</p>
                        <p className="font-medium text-red-500">{campaign.failed?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500">Progress</p>
                        <p className="font-medium">
                          {campaign.total_recipients > 0 
                            ? Math.round(((campaign.sent + campaign.failed) / campaign.total_recipients) * 100) 
                            : 0}%
                        </p>
                      </div>
                    </div>
                    
                    {/* Progress bar */}
                    <div className="w-full bg-zinc-700 rounded-full h-2 mb-3">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{ 
                          width: `${campaign.total_recipients > 0 
                            ? ((campaign.sent + campaign.failed) / campaign.total_recipients) * 100 
                            : 0}%` 
                        }}
                      />
                    </div>
                    
                    {/* Actions */}
                    {campaign.status === 'running' && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => pauseResumeCampaign(campaign.id, 'pause')}
                        className="border-yellow-600 text-yellow-500 hover:bg-yellow-600/20"
                      >
                        <Pause className="w-3 h-3 mr-1" /> Pause
                      </Button>
                    )}
                    {(campaign.status === 'paused_manual' || campaign.status === 'paused_quota') && (
                      <Button 
                        size="sm" 
                        onClick={() => pauseResumeCampaign(campaign.id, 'resume')}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Play className="w-3 h-3 mr-1" /> Resume
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AIDispatcherAdmin;
